import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';
import * as Yup from 'yup';
import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import { Flexbox } from '../../../../../../../../../components/layout/Flexbox';
import { Show } from '../../../../../../../../../components/Show';
import { CardContainer } from './shared/StyledCardContainer';
import { ControlledTextField } from './shared/common';

export const SpecifiedEventExclusionV2Modal = ({
  data: modalProps,
  close,
}: SpecifiedEventExclusionV2ModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<SpecifiedEventV2FormValues>({
    defaultValues: {
      exclusions: modalProps.exclusions,
    },
    resolver: yupResolver(schema),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'exclusions',
  });

  const handleAddExclusion = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      eventReference: '',
      eventDescription: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    if (formMethods.getValues().exclusions[0]?.eventReference) {
      return;
    }

    handleAddExclusion({ scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Specified Event Exclusions
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ControlledTextField
                      label="Event Reference"
                      required
                      name={`exclusions.${index}.eventReference`}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ControlledTextField
                      minRows={4}
                      multiline
                      required
                      label="Event Description"
                      name={`exclusions.${index}.eventDescription`}
                    />
                  </Grid>
                </Grid>
                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <DialogActions>
          <Flexbox>
            <Button
              startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
              className="contrast-text"
              onClick={() => handleAddExclusion({ scroll: true })}
            >
              Add Event
            </Button>
            <Box>
              <CbButton onClick={close} styleName="cancel">
                Cancel
              </CbButton>
              <CbButton type="submit" styleName="ctaButton">
                Save
              </CbButton>
            </Box>
          </Flexbox>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export const SpecifiedEventExclusionV2ModalConfig = {
  SpecifiedEventExclusionV2Modal: {
    component: SpecifiedEventExclusionV2Modal,
    config: {
      title: 'Specified Event Exclusion Endorsement',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

type SpecifiedEventExclusionV2ModalProps = {
  data: {
    onSubmit: (formValues: SpecifiedEventV2FormValues) => void;
    exclusions: SpecifiedEventV2Exclusion[];
  };
  close: () => void;
};

export type SpecifiedEventV2FormValues = {
  exclusions: SpecifiedEventV2Exclusion[];
};

export type SpecifiedEventV2Exclusion = {
  eventReference: string;
  eventDescription: string;
};

const schema = Yup.object().shape({
  exclusions: Yup.array().of(
    Yup.object().shape({
      eventReference: Yup.string().trim().label('Event reference').required(),
      eventDescription: Yup.string()
        .trim()
        .label('Event description')
        .required(),
    })
  ),
});
